import React, { Component, Fragment } from 'react'
import { Formik, Field } from 'formik'

// styles
import { ModalHeader, ScreenWrapper, ModalContainer, CloseIcon, ModalWrapper } from './styles'

// utils and config
import constants from 'config/constants'
import stringUtils from 'utils/string'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import FieldRow from 'components/FieldRow/FieldRow'
import FileUpload from 'components/FileUpload/FileUpload'
import LargeButton from 'components/LargeButton/LargeButton'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'
import NumberInput from 'components/NumberInput/NumberInput'
import PhoneNumberInput from 'components/PhoneNumberInput'
import Select from 'components/Select/Select'
import TextInput from 'components/TextInput/TextInput'
import ErrorMessage from 'components/ErrorMessage'

const isDev = process.env.NODE_ENV === 'development'

const initializeValues = ({ campaign, formValues }) => {
  const maxLearnersPerSchoolValue = campaign.learners_per_registration

  if (isDev) {
    const adjustedFormValues = { ...formValues }

    Array.from({ length: maxLearnersPerSchoolValue }, (_, i) => {
      const prefix = `learner_${i + 1}`

      adjustedFormValues[`${prefix}_f_name`] = `Learner${['A', 'B', 'C', 'D'][i]}`
      adjustedFormValues[`${prefix}_l_name`] = `Surname${['A', 'B', 'C', 'D'][i]}`
      adjustedFormValues[`${prefix}_race`] = constants.GS_LEARNER_RACES[i].value
      adjustedFormValues[`${prefix}_gender`] = constants.GS_LEARNER_GENDERS[i].value
      adjustedFormValues[`${prefix}_grade`] = constants.GS_LEARNER_GRADES[i].value
      adjustedFormValues[`${prefix}_parent_mobile_number`] = '+27831234567'
      adjustedFormValues[`${prefix}_parent_consent_form`] =
        'https://cdn.browniepoints.africa/growsmart/school_logo.png'
      adjustedFormValues[`${prefix}_parent_email`] = `parent${i}@email.com`
      adjustedFormValues[`${prefix}_avg_mark_prev_year`] = Math.floor(Math.random() * 100)
    })

    return { ...adjustedFormValues, batch_count: maxLearnersPerSchoolValue }
  }

  return { ...formValues, batch_count: maxLearnersPerSchoolValue }
}

const validate = values => {
  const errors = {}

  const enrolmentFields = {
    f_name: "Please add the learner's first name",
    l_name: "Please add the learner's last name",
    race: "Please include the learner's race",
    gender: "Please include the learner's gender",
    grade: "Please add the learner's grade",
    parent_email: "Please include an email address or contact number for the learner's parent(s)",
    parent_mobile_number:
      "Please include an email address or contact number for the learner's parent(s)"
  }

  const batchCount = values.batch_count

  // validate average report mark + file fields
  Array.from({ length: batchCount }, (_, i) => {
    const prefix = `learner_${i + 1}`
    const avgMarkFieldKey = `${prefix}_avg_mark_prev_year`
    const avgMarkReportFileFieldKey = `${prefix}_avg_mark_prev_year_report`
    const parentConsentFileFieldKey = `${prefix}_parent_consent_form`

    if (!values[avgMarkFieldKey]) {
      // errors[avgMarkFieldKey] = 'Please include an average mark'
    }

    if (!values[avgMarkReportFileFieldKey]) {
      // errors[avgMarkReportFileFieldKey] = 'Please include a report'
    }

    if (!values[parentConsentFileFieldKey]) {
      // errors[parentConsentFileFieldKey] = 'Please include a consent form'
    }
  })

  Array.from({ length: batchCount }, (_, i) => {
    const prefix = `learner_${i + 1}`

    Object.keys(enrolmentFields).map(f => {
      const fieldKey = `${prefix}_${f}`
      const isContactField = ['parent_mobile_number', 'parent_email'].includes(f)

      const numberField = `${prefix}_parent_mobile_number`
      const emailField = `${prefix}_parent_email`

      if (isContactField) {
        const neitherEmailOrNumber = !values[emailField] && !values[numberField]
        if (neitherEmailOrNumber) {
          errors[fieldKey] =
            "Please include an email address or contact number for the learner's parent(s)"
        }
      } else if (!values[fieldKey]) {
        errors[fieldKey] = enrolmentFields[f]
      }
    })
  })

  const uniqueLearnerNames = []

  Array.from({ length: batchCount }, (_, i) => {
    const prefix = `learner_${i + 1}`

    Object.keys(enrolmentFields).map(f => {
      const isLastNameField = f === 'l_name'
      const fieldKey = `${prefix}_${f}`
      const learnerName = values[`${prefix}_f_name`] + ' ' + values[`${prefix}_l_name`]
      const learnerAddedTwice = uniqueLearnerNames.includes(learnerName)

      if (isLastNameField && learnerAddedTwice) {
        errors[fieldKey] = 'You have added the same learner more than once'
      } else if (isLastNameField) {
        uniqueLearnerNames.push(learnerName)
      }
    })
  })

  return errors
}

const EnrolmentError = ({ hasEnrolmentError }) => {
  if (!hasEnrolmentError) return null

  return (
    <ErrorMessage
      scrollOnMount={true}
      text={
        'One or more of the learners you attempted to enrol has already been enrolled previously and cannot take part in this competition. Please reload this page and enrol a different learner or learners.'
      }
    />
  )
}

class CampaignSignupModal extends Component {
  state = { showPastLearners: false }

  render() {
    const { showPastLearners } = this.state
    const { hideModal, campaignApplicationOpts = {} } = this.props
    const { formValues, campaign = {}, hasEnrolmentError } = campaignApplicationOpts
    const { isSubmitting } = formValues

    const maxLearnersPerSchoolValue = campaign.learners_per_registration
    const pastEnrolments = campaign.historical_enrolments || []
    const initializedValues = initializeValues({ campaign, formValues })

    return (
      <ScreenWrapper>
        <ModalWrapper>
          <ModalContainer>
            <CloseIcon dark size={30} onClick={hideModal} />

            <ModalHeader>Enrol learners to {campaign.title}</ModalHeader>

            <p style={{ maxWidth: '500px' }}>
              You must enrol {maxLearnersPerSchoolValue}{' '}
              {maxLearnersPerSchoolValue === 1 ? 'learner' : 'learners'}. Please note that providing
              incorrect learner information may result in disqualification from this competition.
              Please also note that no learners that have been enrolled in any competition before
              can be enrolled.
            </p>

            <ConditionalDisplay displayWhen={[!showPastLearners, pastEnrolments.length]}>
              <LinkButton
                onClick={() => {
                  this.setState({ showPastLearners: true })
                }}
              >
                Show all {pastEnrolments.length} past learners from all years
              </LinkButton>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[showPastLearners, pastEnrolments.length]}>
              <p style={{ maxWidth: '500px' }}>
                {pastEnrolments.map(learner => {
                  return (
                    <div>
                      <span>
                        {learner.l_name}, {learner.f_name}
                      </span>
                    </div>
                  )
                })}
              </p>
            </ConditionalDisplay>

            <Formik
              initialValues={initializedValues}
              enableReinitialize
              validate={validate}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={values => {
                if (isSubmitting) return true
                campaignApplicationOpts.signUp(values)
              }}
              render={({ values, errors, handleSubmit, setFieldError, setFieldValue }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <Fragment>
                      {Array.from({ length: values.batch_count }, () => {}).map((_, i) => (
                        <Fragment key={`campaign_learner_q_${i}`}>
                          <h2>Learner {i + 1}</h2>

                          <FieldRow>
                            <Field
                              name={`learner_${i + 1}_f_name`}
                              placeholder="First name"
                              label="Full, correct learner first name"
                              component={TextInput}
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              name={`learner_${i + 1}_l_name`}
                              placeholder="Last name"
                              label="Full, correct learner last name"
                              component={TextInput}
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              name={`learner_${i + 1}_race`}
                              placeholder="Select race"
                              label="Learner's race"
                              component={Select}
                              options={constants.GS_LEARNER_RACES}
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              name={`learner_${i + 1}_gender`}
                              placeholder="Select gender"
                              label="Learner's gender"
                              component={Select}
                              options={constants.GS_LEARNER_GENDERS}
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              name={`learner_${i + 1}_grade`}
                              placeholder="Select grade"
                              label="Learner's grade"
                              component={Select}
                              options={constants.GS_LEARNER_GRADES}
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              component={TextInput}
                              label="Email address of parent"
                              name={`learner_${i + 1}_parent_email`}
                              placeholder="Parent's email address"
                              type="email"
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              component={PhoneNumberInput}
                              label="Mobile number of parent"
                              name={`learner_${i + 1}_parent_mobile_number`}
                              placeholder="Parent's mobile number"
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              name={`learner_${i + 1}_avg_mark_prev_year`}
                              label={
                                'Final average grade for the previous year (Term 1, Term 2, Term 3, Final)'
                              }
                              placeholder={'Add mark'}
                              component={NumberInput}
                              min="0"
                              max="100"
                              requiredField
                              shortNumberField
                            />
                          </FieldRow>

                          <FieldRow>
                            <Field
                              buttonText="Select consent form"
                              component={FileUpload}
                              cropOpts={{ aspect: 'none' }}
                              disabled={isSubmitting}
                              label="Parent consent form (optional)"
                              name={`learner_${i + 1}_parent_consent_form`}
                              onChange={event => {
                                setFieldValue(
                                  `learner_${i + 1}_parent_consent_form`,
                                  event.target.files[0]
                                )
                              }}
                              placeholder={
                                values[`learner_${i + 1}_parent_consent_form`]
                                  ? stringUtils.fileName(
                                      values[`learner_${i + 1}_parent_consent_form`]
                                    )
                                  : 'Upload consent form'
                              }
                              // requiredField
                              setFieldError={setFieldError}
                              setFieldValue={setFieldValue}
                            />
                          </FieldRow>
                        </Fragment>
                      ))}

                      <EnrolmentError hasEnrolmentError={hasEnrolmentError} />

                      <ConditionalDisplay displayWhen={[Object.keys(errors).length > 0]}>
                        <ErrorMessage
                          scrollOnMount={true}
                          text="Please correct the form errors above"
                        />
                      </ConditionalDisplay>

                      <FieldRow>
                        <LargeButton type="submit" disabled={isSubmitting}>
                          {hasEnrolmentError
                            ? 'Cannot process enrolment'
                            : isSubmitting
                            ? 'Enrolling'
                            : 'Enrol'}
                        </LargeButton>
                      </FieldRow>
                    </Fragment>
                  </form>
                )
              }}
            />
          </ModalContainer>
        </ModalWrapper>
      </ScreenWrapper>
    )
  }
}

export default CampaignSignupModal
